import { AfterViewInit, Component, computed, DestroyRef, ElementRef, HostListener, inject, input, InputSignal, output, Signal, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Psychologist } from '../../models/psychologists';
import { Log } from '../../models/log';
import { UsersSharingLogsComponent } from '../users-sharing-logs/users-sharing-logs.component';
import TYPES from '../../constants/types';
import { LoadingComponent } from '../loading/loading.component';
import { ConfigService } from '../../services/config.service';

interface SharedLogItem {
  logs: Log;
}

interface MoodPoint {
  id: string;
  label: string;
  x: number;
  y: number;
  color: string;
  backgroundColor: string;
  image: string;
  dateLabel: string;
  timeLabel: string;
}

interface MoodSegment {
  id: string;
  path: string;
  startColor: string;
  endColor: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

@Component({
  selector: 'app-mood-progress',
  imports: [
    TranslatePipe,
    UsersSharingLogsComponent,
    LoadingComponent
  ],
  templateUrl: './mood-progress.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mood-progress.component.scss'
})
export class MoodProgressComponent implements AfterViewInit {
  private readonly chartHeight = 360;
  private readonly minChartWidth = 420;
  private readonly chartPaddingStart = 24;
  private readonly chartPaddingTop = 44;
  private readonly chartPaddingBottom = 44;
  private readonly chartPaddingEnd = 18;
  private readonly maxEntries = 30;
  private readonly minPointSpacing = 46;
  private readonly translate = inject(TranslateService);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chartScroll = viewChild<ElementRef<HTMLDivElement>>('chartScroll');
  private readonly chartViewportWidth: WritableSignal<number> = signal(300);

  yourMindLogIsActive: InputSignal<boolean> = input<boolean>(true);
  sharedWithYouIsActive: InputSignal<boolean> = input<boolean>(false);
  listLogs: InputSignal<Log[] | undefined> = input();
  listShared: InputSignal<SharedLogItem[] | undefined> = input();
  psychologist: InputSignal<Psychologist | undefined> = input();
  loadingLogs: InputSignal<boolean> = input(false);
  loadingSharedLogs: InputSignal<boolean> = input(false);
  filterBy: InputSignal<string> = input('');

  userSelected = output<string>();

  readonly moodLevels: Record<string, number> = {
    'moods.super_negative': 1,
    'moods.negative': 2,
    'moods.neutral': 3,
    'moods.positive': 4,
    'moods.super_positive': 5
  };

  readonly moodClasses: Record<string, string> = TYPES.APPLICATION.MOODS.reduce(
    (accumulator: Record<string, string>, item: { label: string; class: string }) => {
      accumulator[item.label] = item.class;
      return accumulator;
    },
    {}
  );

  readonly moodImages: Record<string, string> = TYPES.APPLICATION.MOODS.reduce(
    (accumulator: Record<string, string>, item: { label: string; image: string }) => {
      accumulator[item.label] = item.image;
      return accumulator;
    },
    {}
  );

  readonly moodColors: Record<string, { stroke: string; fill: string }> = {
    'super-positive': {
      stroke: '#F0D100',
      fill: '#FFF08A'
    },
    positive: {
      stroke: '#55CF7A',
      fill: '#9BE8AF'
    },
    neutral: {
      stroke: '#7382F6',
      fill: '#B5BEFF'
    },
    negative: {
      stroke: '#A970F8',
      fill: '#D5B6FF'
    },
    'super-negative': {
      stroke: '#EA73DE',
      fill: '#F4A3EC'
    }
  };

  readonly emptyStateKey: Signal<string> = computed(() => {
    if (this.sharedWithYouIsActive() && !this.filterBy()) {
      return 'historical.select_name';
    }

    return 'historical.mood_progress_empty';
  });

  readonly visibleLogs: Signal<Log[]> = computed(() => {
    const source = this.sharedWithYouIsActive()
      ? (this.listShared() ?? []).map((item) => item.logs)
      : (this.listLogs() ?? []);

    return source
      .filter((log) => !!log?.mood && !!log?.created_at)
      .slice(0, this.maxEntries)
      .reverse();
  });

  readonly gridColumns: Signal<number[]> = computed(() => {
    return this.visibleLogs().map((_, index) => index);
  });

  readonly chartContentWidth: Signal<number> = computed(() => {
    const total = this.visibleLogs().length;
    const viewportWidth = this.chartViewportWidth();

    if (total <= 1) {
      return Math.max(viewportWidth, this.minChartWidth);
    }

    const paddedWidth = this.chartPaddingStart + this.chartPaddingEnd + (total - 1) * this.minPointSpacing;
    return Math.max(viewportWidth, this.minChartWidth, paddedWidth);
  });

  readonly markerSize: Signal<number> = computed(() => 24);

  readonly markerGlowRadius: Signal<number> = computed(() => {
    return this.markerSize() / 2 + 4;
  });

  readonly segmentStrokeWidth: Signal<number> = computed(() => 18);

  readonly chartPoints: Signal<MoodPoint[]> = computed(() => {
    const logs = this.visibleLogs();

    if (logs.length === 0) {
      return [];
    }

    const width = this.chartContentWidth() - this.chartPaddingStart - this.chartPaddingEnd;
    const height = this.chartHeight - this.chartPaddingTop - this.chartPaddingBottom;
    const stepX = logs.length > 1 ? width / (logs.length - 1) : 0;

    return logs.map((log, index) => {
      const moodLabel = log.mood ?? 'moods.neutral';
      const moodClass = this.moodClasses[moodLabel] ?? 'neutral';
      const level = this.moodLevels[moodLabel] ?? 3;
      const x = this.chartPaddingStart + stepX * index;
      const y = this.chartPaddingTop + ((5 - level) / 4) * height;
      const palette = this.moodColors[moodClass] ?? this.moodColors['neutral'];

      return {
        id: log.id ?? `${index}`,
        label: moodLabel,
        x,
        y,
        color: palette.stroke,
        backgroundColor: palette.fill,
        image: this.moodImages[moodLabel] ?? this.moodImages['moods.neutral'],
        dateLabel: this.formatDate(log.created_at),
        timeLabel: this.formatTime(log.created_at)
      };
    });
  });

  readonly chartSegments: Signal<MoodSegment[]> = computed(() => {
    const points = this.chartPoints();

    if (points.length < 2) {
      return [];
    }

    return points.slice(0, -1).map((point, index) => {
      const nextPoint = points[index + 1];
      const controlX = (point.x + nextPoint.x) / 2;

      return {
        id: `${point.id}-${nextPoint.id}`,
        path: `M ${point.x} ${point.y} C ${controlX} ${point.y}, ${controlX} ${nextPoint.y}, ${nextPoint.x} ${nextPoint.y}`,
        startColor: point.color,
        endColor: nextPoint.color,
        x1: point.x,
        y1: point.y,
        x2: nextPoint.x,
        y2: nextPoint.y
      };
    });
  });

  filterByUser(userId: string) {
    this.userSelected.emit(userId);
  }

  ngAfterViewInit(): void {
    this.updateChartViewportWidth();

    const element = this.chartScroll()?.nativeElement;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => this.updateChartViewportWidth());
    resizeObserver.observe(element);
    this.destroyRef.onDestroy(() => resizeObserver.disconnect());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateChartViewportWidth();
  }

  private formatDate(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(this.getLocale(), {
      day: '2-digit',
      month: '2-digit'
    }).format(date);
  }

  private formatTime(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const locale = this.getLocale();
    const isPortuguese = locale.toLowerCase().startsWith('pt');

    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      hour12: !isPortuguese,
      minute: '2-digit'
    }).format(date);
  }

  private getLocale(): string {
    return this.configService.preferedLanguage() || this.translate.currentLang || 'en';
  }

  private updateChartViewportWidth(): void {
    const width = this.chartScroll()?.nativeElement.clientWidth ?? 300;

    if (width > 0) {
      this.chartViewportWidth.set(width);
    }
  }
}
